/**
 * Hisobot davri presetlari — markazdagi `PeriodBar` va hisobot
 * sahifasining filtr paneli (E1) BITTA funksiyadan o'qiydi.
 *
 * ⚠️ D1 va E1 bu faylni parallel yaratadi — imzo reja bo'yicha AYNAN
 * bir xil (`presetRange(preset, today)`), merge'da bittasi qoladi.
 */
export type PeriodPreset = "today" | "yesterday" | "thisWeek" | "thisMonth" | "lastMonth";

/** Segment tartibi — UI shu tartibda chizadi. */
export const PERIOD_PRESETS: readonly PeriodPreset[] = [
	"today",
	"yesterday",
	"thisWeek",
	"thisMonth",
	"lastMonth",
];

export function isPeriodPreset(value: unknown): value is PeriodPreset {
	return typeof value === "string" && (PERIOD_PRESETS as readonly string[]).includes(value);
}

const KUN_MS = 86_400_000;

function sanaUtc(sana: string): Date {
	const [yil, oy, kun] = sana.split("-").map(Number);

	return new Date(Date.UTC(yil ?? 1970, (oy ?? 1) - 1, kun ?? 1));
}

function formatla(sana: Date): string {
	return sana.toISOString().slice(0, 10);
}

/**
 * Presetni `YYYY-MM-DD` oraliqqa aylantiradi.
 *
 * ⚠️ `today` PARAMETR — funksiya vaqtni o'zi o'qimaydi (oy chegarasida
 * flaky test bo'lmasin, `defaultReportRange` naqshi). Arifmetika UTC da:
 * sana satri zonasiz kalendar kuni, brauzer zonasi aralashmaydi.
 *
 * ⚠️ Hafta DUSHANBADAN boshlanadi (ISO, `EXTRACT(ISODOW)` bilan bir xil),
 * `dayjs` lokaliga tayanilmaydi — u standart holda yakshanbadan sanaydi.
 * `thisMonth` — 1-kundan bugungacha (`defaultReportRange` bilan bir xil),
 * `lastMonth` — to'liq oldingi oy.
 */
export function presetRange(
	preset: PeriodPreset,
	today: string
): { dateFrom: string; dateTo: string } {
	const bugun = sanaUtc(today);

	switch (preset) {
		case "today":
			return { dateFrom: today, dateTo: today };
		case "yesterday": {
			const kecha = formatla(new Date(bugun.getTime() - KUN_MS));
			return { dateFrom: kecha, dateTo: kecha };
		}
		case "thisWeek": {
			// getUTCDay: 0 = yakshanba → ISO 7.
			const isoKun = bugun.getUTCDay() || 7;
			return {
				dateFrom: formatla(new Date(bugun.getTime() - (isoKun - 1) * KUN_MS)),
				dateTo: today,
			};
		}
		case "thisMonth":
			return {
				dateFrom: formatla(new Date(Date.UTC(bugun.getUTCFullYear(), bugun.getUTCMonth(), 1))),
				dateTo: today,
			};
		case "lastMonth":
			return {
				// Date.UTC oy −1 ni o'tgan yilning dekabriga o'tkazadi.
				dateFrom: formatla(new Date(Date.UTC(bugun.getUTCFullYear(), bugun.getUTCMonth() - 1, 1))),
				// Joriy oyning 0-kuni — oldingi oyning oxirgi kuni.
				dateTo: formatla(new Date(Date.UTC(bugun.getUTCFullYear(), bugun.getUTCMonth(), 0))),
			};
	}
}

/**
 * Oraliqqa AYNAN mos keladigan birinchi preset (segment belgisi).
 *
 * ⚠️ URL dagi `preset` ga ko'r-ko'rona ishonilmaydi: kecha saqlangan
 * `?preset=today&dateFrom=…` havolasi bugun ochilsa sanalar endi
 * «bugun» emas — segment YOLG'ON belgilangan bo'lardi.
 */
export function matchPreset(
	range: { dateFrom: string; dateTo: string },
	today: string
): PeriodPreset | null {
	return (
		PERIOD_PRESETS.find((preset) => {
			const oraliq = presetRange(preset, today);

			return oraliq.dateFrom === range.dateFrom && oraliq.dateTo === range.dateTo;
		}) ?? null
	);
}
